// src/App.jsx
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

/* ADMIN */
import Dashboard from "./components/Dashboard";
import BookingForm from "./components/BookingForm";
import TripsPage from "./components/TripsPage";
import OrdersPage from "./components/OrdersPage";
import BottomNav from "./components/BottomNav";
import CashbookPage from "./components/CashBook";
import DriverPage from "./components/Drivers";
import Navbar from "./components/NavBar";
import ProfilePage from "./components/Profile";
import LoginPage from "./components/LoginPage";
import ProtectedRoute from "./components/ProtectedRoute";
import ForgotPassword from "./components/ForgetPassword";
import UpdatePassword from "./components/UpdatePassword";
import VehicleListPage from "./components/VehicleListPage";
import VehicleMaintenancePage from "./components/VehicleMaintenancePage";
import EnquiryPage from "./components/EnquiryPage";
import ExportPDF from "./components/ExportPDF";

/* PUBLIC SITE */
import Header from "./homeComponents/layout/Header";
import Home from "./pages/Home";
import Footer from "./homeComponents/layout/Footer";
import BottomNavigation from "./homeComponents/layout/BottomNavigation";
import ToursAndTravels from "./pages/ToursAndTravels";
import Vehicles from "./pages/Vehicles";
import Motors from "./pages/Motors";
import Consultancy from "./pages/Consultancy";
import { Contact } from "lucide-react";
import RideBookingsPublicView from "./homeComponents/RideBookingsPublicView";
import AppointmentsView from "./homeComponents/appointment/AppointmentsView";
import SellingVehiclesPage from "./pages/Motors/SellingVehiclesPage"; 

const PUBLIC_PATHS = [
  "/",
  "/tours",
  "/vehicles",
  "/motors",
  "/consultancy",
  "/contact",
  "/motors/sell",
  "/motors/selling",
  "/appointments-view",
  "/ride-bookings-view",
];

/* ============================================================
   SCROLL TO TOP — resets scroll on every route change
   ============================================================ */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    /* Instant reset — no smooth animation on route change */
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  return null;
}

/* ============================================================
   APP SHELL — public vs admin chrome
   ============================================================ */
function AppShell({ session }) {
  const location = useLocation();
  const isPublicPath = PUBLIC_PATHS.includes(location.pathname);

  /* Admin chrome (Navbar + BottomNav) only on admin paths */
  const showAdminChrome = session && !isPublicPath;

  return (
    <div className="min-h-screen relative pb-0 bg-gray-100">
      {showAdminChrome && <Navbar />}

      <Routes>
        {/* ==============================
            PUBLIC NJV SITE
        ============================== */}

        {/* SELL YOUR VEHICLE */}
        {/* <Route
          path="/motors/sell"
          element={
            <>
              <Header />
              <SellVehiclePage />
              <Footer />
              <BottomNavigation />
            </>
          }
        /> */}

        {/* BROWSE VEHICLES */}
        <Route
          path="/motors/selling"
          element={
            <>
              <Header />
              <SellingVehiclesPage />
              <Footer />
              <BottomNavigation />
            </>
          }
        />

        {/* APPOINTMENTS VIEW */}
        <Route
          path="/appointments-view"
          element={
            <>
              <Header />
              <AppointmentsView />
              <Footer />
              <BottomNavigation />
            </>
          }
        />

        {/* RIDE BOOKINGS VIEW */}
        <Route
          path="/ride-bookings-view"
          element={
            <>
              <Header />
              <RideBookingsPublicView />
              <Footer />
              <BottomNavigation />
            </>
          }
        />

        {/* HOME */}
        <Route
          path="/"
          element={
            <>
              <Header />
              <Home />
              <Footer />
              <BottomNavigation />
            </>
          }
        />

        {/* TOURS */}
        <Route
          path="/tours"
          element={
            <>
              <Header />
              <ToursAndTravels />
              <Footer />
              <BottomNavigation />
            </>
          }
        />

        {/* VEHICLES */}
        <Route
          path="/vehicles"
          element={
            <>
              <Header />
              <Vehicles />
              <Footer />
              <BottomNavigation />
            </>
          }
        />

        {/* MOTORS */}
        <Route
          path="/motors"
          element={
            <>
              <Header />
              <Motors />
              <Footer />
              <BottomNavigation />
            </>
          }
        />

        {/* CONSULTANCY */}
        <Route
          path="/consultancy"
          element={
            <>
              <Header />
              <Consultancy />
              <Footer />
              <BottomNavigation />
            </>
          }
        />

        {/* CONTACT */}
        <Route
          path="/contact"
          element={
            <>
              <Header />
              <Contact />
              <Footer />
              <BottomNavigation />
            </>
          }
        />

        {/* ==============================
            ADMIN LOGIN
        ============================== */}
        <Route
          path="/admin"
          element={
            session ? <Navigate to="/dashboard" replace /> : <LoginPage />
          }
        />

        {/* ==============================
            ADMIN PROTECTED ROUTES
        ============================== */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/booking"
          element={
            <ProtectedRoute>
              <BookingForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trips"
          element={
            <ProtectedRoute>
              <TripsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cashBook"
          element={
            <ProtectedRoute>
              <CashbookPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/driversPage"
          element={
            <ProtectedRoute>
              <DriverPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vehicle/:id"
          element={
            <ProtectedRoute>
              <VehicleMaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vehicleList"
          element={
            <ProtectedRoute>
              <VehicleListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/enquiry"
          element={
            <ProtectedRoute>
              <EnquiryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/export"
          element={
            <ProtectedRoute>
              <ExportPDF />
            </ProtectedRoute>
          }
        />

        {/* Auth helpers (public, no chrome) */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />

        {/* ==============================
            CATCH-ALL
        ============================== */}
        <Route
          path="*"
          element={
            session ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Routes>

      {showAdminChrome && <BottomNav />}
    </div>
  );
}

/* ============================================================
   APP — session bootstrap
   ============================================================ */
function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setLoading(false);
    };

    getSession();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
      }
    );

    return () => listener.subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Checking session...
      </div>
    );
  }

  return (
    <Router>
      {/* Scroll to top on every route change — must be inside Router */}
      <ScrollToTop />
      <AppShell session={session} />
    </Router>
  );
}

export default App;